import React, {
  createContext, FC, useContext, useReducer,
} from 'react';
import CsvReducer from '../reducers/statusUpdate';
import { ActionFromPayloadMap, PlanResponseLog } from '../config/types';
import { StatusUpdateMode } from '../config/constants';

export type StatusUpdatePlanResponse = {
  create: number;
  update: number;
  unchanged: number;
  updates: {
    created: AssetStatusUpdate[];
    updated: AssetStatusUpdate[];
    unchanged: AssetStatusUpdate[];
  };
  logging: {
    errors: PlanResponseLog[];
    warnings: PlanResponseLog[];
    info: PlanResponseLog[];
    errorCount: number;
    warningCount: number;
    infoCount: number;
  };
};

type AssetStatusUpdate = {
  label: string;
  type: string;
  status: string;
  date: string; // ISO timestamp
};

export type StatusUpdateStateType = {
  isPlanningStatuses: boolean
  isApplyingStatuses: boolean
  plannedUpdatedStatus: StatusUpdatePlanResponse | null
  fileName: string | null
  importTime: Date | null
  fileContents: string | null
  statusUpdateMode: StatusUpdateMode
  filtersStatusUpdate: Array<string>
  showChangesOnly: boolean
  error: Error | null
};

const initialState: StatusUpdateStateType = {
  isPlanningStatuses: false,
  isApplyingStatuses: false,
  plannedUpdatedStatus: null,
  fileName: null,
  importTime: null,
  fileContents: null,
  statusUpdateMode: 'default',
  filtersStatusUpdate: [],
  showChangesOnly: false,
  error: null,
};

export type StatusUpdateActionPayloadMap = {
  SET_STATUS_UPDATE_FILE: { name: string; contents: string };
  SET_IS_PLANNING_STATUS_UPDATE: boolean;
  SET_STATUS_UPDATE_PLAN: StatusUpdateStateType['plannedUpdatedStatus'];
  SET_IS_APPLYING_STATUS_UPDATE: boolean;
  SET_STATUS_UPDATE_ERROR: StatusUpdateStateType['error'];
  RESET_IMPORT_STATUS_UPDATE: undefined;
  SET_STATUS_UPDATE_MODE: StatusUpdateMode;
};

export type StatusUpdateAction = ActionFromPayloadMap<StatusUpdateActionPayloadMap>;

export const StatusUpdateContext = createContext({
  state: initialState,
  dispatch: (() => { }) as React.Dispatch<StatusUpdateAction>,
});

export const useStatusUpdate = (): [StatusUpdateStateType, React.Dispatch<StatusUpdateAction>] => {
  const { state, dispatch } = useContext(StatusUpdateContext);
  return [state, dispatch];
};

export const StatusUpdateProvider:FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [state, dispatch] = useReducer(CsvReducer, initialState);

  return (
    <StatusUpdateContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        state,
        dispatch,
      }}
    >
      {children}
    </StatusUpdateContext.Provider>
  );
};
